import { useEffect, useRef, useState } from "react";
import { CASE_STUDIES } from "../data/case-studies";
import styles from "./CaseStudies.module.css";

const ROTATE_MS = 8000;

export function CaseStudies() {
  const [active, setActive] = useState(0);
  // Rotation is opt-in: users can pick a story from the tablist at their own
  // pace. Defaulting to on forces a 40-second wait to see every story and was
  // flagged in the design review.
  const [autoRotate, setAutoRotate] = useState(false);
  const timerRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      visibleRef.current = rect.top < vh * 0.6 && rect.bottom > vh * 0.3;
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = () => {
    if (timerRef.current) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timerRef.current = window.setInterval(() => {
      if (visibleRef.current && !document.hidden) {
        setActive((current) => (current + 1) % CASE_STUDIES.length);
      }
    }, ROTATE_MS);
  };

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    if (!autoRotate) return;
    start();
    return stop;
  }, [autoRotate]);

  // Pointer / keyboard hover pauses the timer but does not change the
  // explicit paused state — when the user moves away, it auto-resumes
  // unless they've toggled rotation off.
  const hoverPause = () => stop();
  const hoverResume = () => {
    if (autoRotate) start();
  };

  return (
    <section
      ref={sectionRef}
      id="work"
      className={styles.section}
      aria-labelledby="work-heading"
      onMouseEnter={hoverPause}
      onMouseLeave={hoverResume}
      onFocusCapture={hoverPause}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hoverResume();
      }}
    >
      <div className="container-wide">
        <header className={styles.head} data-reveal>
          <div className={styles.headRow}>
            <h2 id="work-heading" className={styles.heading}>
              Real clients, real outcomes.
            </h2>
            <button
              type="button"
              className={styles.rotateToggle}
              aria-pressed={autoRotate}
              onClick={() => setAutoRotate((current) => !current)}
            >
              <span className={styles.toggleDot} aria-hidden="true" />
              <span>{autoRotate ? "Auto-rotating" : "Auto-rotate"}</span>
            </button>
          </div>
          <p className={styles.lede}>
            Highlighted for tailored placement, tone, and long-term grow-out. Stories from clients
            who chose Cathryn for considered color.
          </p>
        </header>

        <div className={styles.grid}>
          {CASE_STUDIES.map((study, i) => {
            const isActive = i === active;
            return (
              <article
                key={study.id}
                id={`study-${study.id}`}
                className={`${styles.study} ${isActive ? styles.studyActive : ""}`}
                role="tabpanel"
                aria-labelledby={`study-tab-${study.id}`}
                aria-hidden={!isActive}
                inert={!isActive ? true : undefined}
              >
                <div className={styles.studyContent}>
                  <div className={styles.quote}>
                    <span className={styles.quoteMark} aria-hidden="true">
                      &ldquo;
                    </span>
                    {study.description}
                  </div>

                  <div className={styles.meta}>
                    <div className={styles.client}>
                      <span className={styles.avatar} aria-hidden="true">
                        {study.client.initials}
                      </span>
                      <div>
                        <p className={styles.clientName}>{study.client.name}</p>
                        <p className={styles.clientRole}>{study.client.role}</p>
                      </div>
                    </div>
                    <div className={styles.tags}>
                      {study.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <h3 className={styles.studyTitle}>{study.title}</h3>
                </div>

                <figure className={styles.media}>
                  <picture>
                    <source type="image/avif" srcSet={study.image.srcSet.split(",")[0]} />
                    <source type="image/webp" srcSet={study.image.srcSet.split(",")[1]} />
                    <img
                      src={study.image.src}
                      alt={`${study.title} transformation`}
                      loading="lazy"
                      decoding="async"
                      width={1040}
                      height={1300}
                    />
                  </picture>
                </figure>
              </article>
            );
          })}
        </div>

        <div className={styles.logoBar} role="tablist" aria-label="Client transformations">
          {CASE_STUDIES.map((study, i) => {
            const isActive = i === active;
            return (
              <button
                key={study.id}
                id={`study-tab-${study.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`study-${study.id}`}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.logoBtn} ${isActive ? styles.logoBtnActive : ""}`}
                onClick={() => setActive(i)}
              >
                <span className={styles.logoMonogram} aria-hidden="true">
                  {study.client.initials}
                </span>
                <span className={styles.logoName}>{study.client.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}